import { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Gift, Star, Heart } from 'lucide-react';
import { differenceInCalendarDays, parseISO, format } from 'date-fns';
import { useEvents } from '../hooks/useEvents';
import { useMembers } from '../hooks/useMembers';
import { AppShell } from '../components/layout/AppShell';
import { PageHeader } from '../components/layout/PageHeader';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { CalendarEntry, Profile } from '../types';
import { formatDisplayDate, formatShortDate, formatTime } from '../lib/dates';

function buildBirthdayEntries(members: Profile[], rangeMonths = 6): CalendarEntry[] {
  const today = new Date();
  const entries: CalendarEntry[] = [];
  members.forEach((m) => {
    if (!m.birthday) return;
    for (const yearOffset of [0, 1]) {
      const [, month, day] = m.birthday.split('-');
      const year = today.getFullYear() + yearOffset;
      const bDate = new Date(year, parseInt(month) - 1, parseInt(day));
      const diffDays = (bDate.getTime() - today.getTime()) / 86400000;
      if (diffDays >= -1 && diffDays <= rangeMonths * 30) {
        entries.push({
          id: `birthday-${m.id}-${year}`,
          title: `${m.full_name.split(' ')[0]}'s Birthday 🎉`,
          date: `${year}-${month}-${day}`,
          type: 'birthday',
          birthdayProfile: m,
        });
        break;
      }
    }
  });
  return entries;
}

function buildAnniversaryEntries(members: Profile[], rangeMonths = 6): CalendarEntry[] {
  const today = new Date();
  const entries: CalendarEntry[] = [];
  const processedPairs = new Set<string>();
  members.forEach((m) => {
    if (!m.anniversary) return;
    const spouseName = m.spouse_name || null;
    const pairKey = m.spouse_user_id ? [m.id, m.spouse_user_id].sort().join('-') : `solo-${m.id}`;
    if (processedPairs.has(pairKey)) return;
    processedPairs.add(pairKey);
    const firstName = m.full_name.split(' ')[0];
    const spouseFirst = spouseName ? spouseName.split(' ')[0] : null;
    const title = spouseFirst
      ? `${firstName} & ${spouseFirst}'s Anniversary 💍`
      : `${firstName}'s Anniversary 💍`;
    for (const yearOffset of [0, 1]) {
      const [, month, day] = m.anniversary.split('-');
      const year = today.getFullYear() + yearOffset;
      const aDate = new Date(year, parseInt(month) - 1, parseInt(day));
      const diffDays = (aDate.getTime() - today.getTime()) / 86400000;
      if (diffDays >= -1 && diffDays <= rangeMonths * 30) {
        entries.push({
          id: `anniversary-${pairKey}-${year}`,
          title,
          date: `${year}-${month}-${day}`,
          type: 'anniversary',
          anniversaryProfile: m,
        });
        break;
      }
    }
  });
  return entries;
}

function relativeDays(dateStr: string): string {
  const days = differenceInCalendarDays(parseISO(dateStr), new Date());
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days <= 6) return `${days} days away`;
  if (days <= 13) return 'Next week';
  if (days <= 30) return `${Math.ceil(days / 7)} weeks away`;
  if (days <= 60) return 'Next month';
  return `${Math.ceil(days / 30)} months away`;
}

/** Group a sorted list of CalendarEntry by "MMMM yyyy" month label */
function groupByMonth(entries: CalendarEntry[]): { month: string; items: CalendarEntry[] }[] {
  const groups: { month: string; items: CalendarEntry[] }[] = [];
  entries.forEach((entry) => {
    const month = format(parseISO(entry.date), 'MMMM yyyy');
    const last = groups[groups.length - 1];
    if (last && last.month === month) {
      last.items.push(entry);
    } else {
      groups.push({ month, items: [entry] });
    }
  });
  return groups;
}

export function CalendarPage() {
  const { events, loading: eventsLoading } = useEvents();
  const { members, loading: membersLoading } = useMembers();
  const navigate = useNavigate();
  const [timedOut, setTimedOut] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setTimedOut(true), 6000);
    return () => clearTimeout(t);
  }, []);
  const loading = (eventsLoading || membersLoading) && !timedOut;

  const calendarEntries = useMemo<CalendarEntry[]>(() => {
    const eventEntries: CalendarEntry[] = events.map((e) => ({
      id: e.id,
      title: e.title,
      date: e.date,
      type: e.type,
      event: e,
    }));
    return [
      ...eventEntries,
      ...buildBirthdayEntries(members),
      ...buildAnniversaryEntries(members),
    ].sort((a, b) => a.date.localeCompare(b.date));
  }, [events, members]);

  const today = format(new Date(), 'yyyy-MM-dd');
  const upcoming = calendarEntries.filter((e) => e.date >= today);
  const past = calendarEntries.filter((e) => e.date < today).reverse();

  const upcomingGroups = groupByMonth(upcoming);
  // Past entries are already reversed (newest first); group them as-is
  const pastGroups = groupByMonth([...past].reverse()).reverse();

  return (
    <AppShell>
      <PageHeader title="Calendar" />
      <div className="px-4 pt-3 pb-6">
        {loading ? (
          <LoadingSpinner />
        ) : calendarEntries.length === 0 ? (
          <EmptyCalendar />
        ) : (
          <>
            {upcomingGroups.length > 0 && (
              <section>
                <SectionLabel label="Upcoming" />
                <div className="mt-3 space-y-5">
                  {upcomingGroups.map(({ month, items }) => (
                    <MonthGroup key={month} month={month}>
                      {items.map((entry) => (
                        <CalendarCard
                          key={entry.id}
                          entry={entry}
                          onClick={entry.event ? () => navigate(`/events/${entry.event!.id}`) : undefined}
                        />
                      ))}
                    </MonthGroup>
                  ))}
                </div>
              </section>
            )}

            {pastGroups.length > 0 && (
              <section className="mt-8">
                <SectionLabel label="Past" />
                <div className="mt-3 space-y-5 opacity-60">
                  {pastGroups.map(({ month, items }) => (
                    <MonthGroup key={month} month={month}>
                      {items.map((entry) => (
                        <CalendarCard
                          key={entry.id}
                          entry={entry}
                          onClick={entry.event ? () => navigate(`/events/${entry.event!.id}`) : undefined}
                        />
                      ))}
                    </MonthGroup>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </AppShell>
  );
}

function SectionLabel({ label }: { label: string }) {
  return (
    <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest px-1">
      {label}
    </p>
  );
}

function MonthGroup({ month, children }: { month: string; children: React.ReactNode }) {
  // Show year only if it's not the current year
  const currentYear = new Date().getFullYear().toString();
  const label = month.endsWith(currentYear)
    ? month.replace(` ${currentYear}`, '')
    : month;

  return (
    <div>
      {/* Month divider */}
      <div className="flex items-center gap-2 mb-2 px-1">
        <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
          {label}
        </span>
        <div className="flex-1 h-px bg-gray-100 dark:bg-gray-800" />
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function CalendarCard({ entry, onClick }: { entry: CalendarEntry; onClick?: () => void }) {
  const isBirthday = entry.type === 'birthday';
  const isAnniversary = entry.type === 'anniversary';
  const isSpecial = entry.type === 'special_event';

  const iconBg = isBirthday ? 'bg-pink-50 dark:bg-pink-950'
    : isAnniversary ? 'bg-rose-50 dark:bg-rose-950'
    : isSpecial ? 'bg-blue-50 dark:bg-blue-950'
    : 'bg-warm-50 dark:bg-gray-800';

  const iconColor = isBirthday ? 'text-pink-400'
    : isAnniversary ? 'text-rose-500'
    : isSpecial ? 'text-blue-400'
    : 'text-warm-500';

  const Icon = isBirthday ? Gift : isAnniversary ? Heart : isSpecial ? Star : Calendar;
  const Wrapper = onClick ? 'button' : 'div';
  const today = format(new Date(), 'yyyy-MM-dd');
  const isUpcoming = entry.date >= today;

  return (
    <Wrapper
      onClick={onClick}
      className={`w-full flex items-center gap-3 bg-white dark:bg-gray-900 rounded-2xl border border-warm-100 dark:border-gray-800 px-4 py-3 shadow-sm text-left ${
        onClick ? 'hover:bg-warm-50 dark:hover:bg-gray-800 active:scale-[0.98] transition-all cursor-pointer' : ''
      }`}
    >
      <div className={`w-10 h-10 ${iconBg} rounded-xl flex items-center justify-center flex-shrink-0`}>
        <Icon size={16} className={iconColor} />
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{entry.title}</p>
        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
          <p className="text-xs text-gray-400 dark:text-gray-500">{formatDisplayDate(entry.date)}</p>
          {isUpcoming && entry.date !== today && (
            <span className="text-xs bg-warm-50 dark:bg-gray-800 text-warm-600 dark:text-warm-400 px-1.5 py-0.5 rounded-full font-medium">
              {relativeDays(entry.date)}
            </span>
          )}
        </div>
      </div>

      <div className="text-right flex-shrink-0">
        <p className="text-xs font-medium text-gray-400 dark:text-gray-500">{formatShortDate(entry.date)}</p>
        {entry.event?.time && (
          <p className="text-xs text-gray-300 dark:text-gray-600 mt-0.5">
            {formatTime(entry.event.time)}
          </p>
        )}
      </div>
    </Wrapper>
  );
}

function EmptyCalendar() {
  return (
    <div className="text-center py-12">
      <div className="text-4xl mb-3">📅</div>
      <p className="font-semibold text-gray-600 dark:text-gray-400">Nothing scheduled yet</p>
      <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Events will appear here once created.</p>
    </div>
  );
}
